export enum Statuses {
  REVIEWING = 'RE',
  DECLINED = 'DL',
  APPROVED = 'OK',
  SUBMITTED = 'SUBMITTED',
  REQUIRED = 'REQUIRED'
}

export enum DocumentTypes {
  ID = 'ID',
  TAX = 'TAX'
}

export interface IAddPaymentAccount {
  bankInfo: IBankInfo;
  documentation: IDocuments;
}

export interface IUploadedFile {
  id: number;
  link: string;
  name: string;
}

export interface IDocument {
  id?: number;
  rejectReason?: IRejectReason;
  status: string;
  file: any;
  name: string;
  url: string;
  url_expires_at?: string | null;
}

export interface IDocuments {
  officialIdStatus?: Statuses;
  officialId?: IDocument[];
  tax?: IDocument[];
  taxStatus?: Statuses;
  officialIdFiles: any[];
  taxFiles: any[];
}

export interface IBankInfoField {
  errors: any[];
  status: string;
  value: string;
}

export interface IBankInfo {
  currency: number;
  account_type: string;
  bank_name: string;
  account_number: string;
  routing_number?: string;
  clabe_number?: string;
  status?: string;
  holder_full_name?: string;
  bank_information?: BankInformation;
}

export interface IPaymentAccount {
  id: number;
  status: Statuses;
  lastDigits: string;
  name: string;
}

export interface IUserPaymentAccount {
  bank_name: string;
  country_code: 'US' | 'MX';
  currency?: number | null;
  routing_number?: string | null;
  account_number: string;
  document_ids: number[];
  holder_full_name: string;
  clabe_number: string;
  account_type: string;
}

export interface UserDocument {
  file_name: string;
  user_document_type_id: number;
  blob: string;
  user_document_status_id: number;
}

export interface UserDocumentType {
  id: number;
  name: string;
  localizable_name: string;
  code: DocumentTypes;
}

export type TaxDocumentFormType = 'W9' | 'W8BEN';
export type TaxIdType = 'ssn' | 'ein';
export type FederalTaxClassification =
  | 'individual'
  | 'c_corp'
  | 's_corp'
  | 'partnership'
  | 'trust_estate'
  | 'llc'
  | 'other';
export type LlcClassification = 'c' | 's' | 'p';

export interface TaxDocumentAddress {
  id?: number;
  street_address: string;
  city: string;
  state?: string | null;
  zip_code?: string | null;
  country: string;
}

export interface TaxDocumentCertification {
  id?: number;
  electronic_signature: boolean;
  signer_name: string;
  signed_at: string;
}

export interface SubmitW9TaxDocumentPayload {
  form_type: 'W9';
  full_legal_name: string;
  address: {
    street_address: string;
    city: string;
    state: string;
    zip_code: string;
    country: string;
  };
  tax_id: {
    type: TaxIdType;
    value: string;
  };
  federal_tax_classification: FederalTaxClassification;
  llc_classification: LlcClassification | null;
  certification: TaxDocumentCertification;
}

export interface SubmitW8BenTaxDocumentPayload {
  form_type: 'W8BEN';
  full_legal_name: string;
  country_of_citizenship: string;
  permanent_residence_address: {
    street_address: string;
    city: string;
    country: string;
  };
  mailing_address_same_as_permanent: boolean;
  mailing_address: {
    same_as_permanent: boolean;
    street_address: string | null;
    city: string | null;
    country: string | null;
  };
  foreign_tax_id_number: string;
  certification: TaxDocumentCertification;
}

export type SubmitTaxDocumentPayload = SubmitW9TaxDocumentPayload | SubmitW8BenTaxDocumentPayload;

export interface BaseTaxDocument {
  id: number;
  form_type: TaxDocumentFormType;
  full_legal_name: string;
  certification: TaxDocumentCertification;
}

export interface W9TaxDocument extends BaseTaxDocument {
  form_type: 'W9';
  address: TaxDocumentAddress;
  tax_id: {
    id?: number;
    type: TaxIdType;
    masked: string;
  };
  federal_tax_classification: FederalTaxClassification;
  llc_classification: LlcClassification | null;
}

export interface W8BenTaxDocument extends BaseTaxDocument {
  form_type: 'W8BEN';
  country_of_citizenship: string;
  permanent_residence_address: TaxDocumentAddress;
  mailing_address_same_as_permanent: boolean;
  mailing_address: TaxDocumentAddress | null;
  foreign_tax_id_number: string;
}

export type TaxDocument = W9TaxDocument | W8BenTaxDocument;

/**/

interface UserDocumentStatus {
  id: number;
  name: string;
  localizable_name: string;
  code: Statuses;
}

export interface AccountDocument {
  id: number;
  user_document_type: UserDocumentType;
  user_document_type_id: number;
  user_document_status: UserDocumentStatus;
  user_document_status_id: number;
  status_reason_en: string;
  status_reason_es: string;
  created: string; // ISO date string format
  file_name: string;
  url: string;
  url_expires_at?: string | null;
  mime_type: string;
  file?: any;
}

export type DocumentFileReference = Pick<AccountDocument, 'id' | 'file_name' | 'url' | 'url_expires_at'>;

/* PAYMENT ACCOUNT */

export interface BankInformationField {
  value: string | null;
  status: string;
  errors: string[];
}

export interface BankInformation {
  bank_name: BankInformationField;
  routing_number: BankInformationField;
  clabe_number: BankInformationField;
  account_number: BankInformationField;
  currency: BankInformationField;
  status: Statuses;
  account_holder: BankInformationField;
}

export interface AccountHolderField {
  value: string;
  status: string;
  errors: string[];
}

export interface AccountHolder {
  name: AccountHolderField;
}

export interface DocumentItem {
  id: number;
  file?: any;
  file_name: string;
  url: string;
  url_expires_at?: string | null;
  status: Statuses;
  errors: string[];
  reject_reason: IRejectReason;
}

export interface IRejectReason {
  id: number;
  name_en: string;
  name_es: string;
  requires_custom_reason: boolean;
}

export interface Document {
  document_type: { code: DocumentTypes; localizable_name: string; name: string };
  errors: any[];
  file_name: string;
  id: number;
  status: Statuses;
  url: string;
  url_expires_at?: string | null;
  reject_reason: IRejectReason;
}

export interface Account {
  id: number;
  account_type: string;
  bank_information: BankInformation;
  account_holder: AccountHolder;
  bank_info_status: string;
  account_holder_name: string;
  documents: Document[];
  overall_status: string;
  created: string;
  default?: boolean;
}

/**/
